import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Rotate3d } from "lucide-react";
import { useQuote } from "../context/QuoteContext";

const ease = [0.16, 1, 0.3, 1];

export default function ProductCard({ product, index = 0, onView }) {
  const { openQuote } = useQuote();
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease, delay: (index % 3) * 0.08 }}
      className="group bg-white border border-[#0A1128]/10 hover:border-[#1F6FEB]/40 flex flex-col will-change-transform transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-[5px] hover:shadow-[0_20px_44px_-26px_rgba(10,17,40,0.4)]"
      data-testid={`product-card-${product.id}`}
    >
      <button
        onClick={() => onView(product)}
        className="relative overflow-hidden bg-[#F1F5F9] aspect-[4/3] flex items-center justify-center"
        aria-label={`View ${product.name}`}
      >
        {product.img ? (
          <img
            src={product.img} alt={product.name} loading="lazy"
            className="h-[82%] w-auto object-contain mix-blend-multiply transition-transform duration-[900ms] ease-out group-hover:scale-[1.08]"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-center px-6 select-none">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#1F6FEB]">{product.categoryName}</span>
            <span className="mt-3 text-4xl font-extrabold tracking-tighter text-[#0A1128]/[0.12] group-hover:text-[#1F6FEB]/30 transition-colors duration-500">
              {product.model || "Clinixport"}
            </span>
          </div>
        )}
        {product.flagship && (
          <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#1F6FEB]">
            <Rotate3d size={12} /> 360°
          </span>
        )}
        <span className="absolute inset-0 bg-[#1F6FEB]/0 group-hover:bg-[#1F6FEB]/[0.04] transition-colors duration-500" />
      </button>

      <div className="p-6 flex flex-col flex-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#1F6FEB]">{product.categoryName}</p>
        <h3 className="mt-2 text-xl font-bold tracking-tight text-[#0A1128] leading-snug">{product.name}</h3>
        {product.model && <p className="mt-1 text-xs font-semibold tracking-wide text-[#94A3B8]">{product.model}</p>}
        <p className="mt-2 text-sm text-[#475569] leading-relaxed flex-1">{product.spec}</p>
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={() => onView(product)}
            data-testid={`view-details-${product.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0A1128] hover:text-[#1F6FEB] transition-colors group/btn"
          >
            View Details <ArrowUpRight size={16} className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </button>
          <span className="w-px h-4 bg-[#0A1128]/15" />
          <button
            onClick={() => openQuote(product.name)}
            data-testid={`quote-${product.id}`}
            className="text-sm font-bold text-[#1F6FEB] hover:text-[#1556B8] transition-colors"
          >
            Request Quote
          </button>
        </div>
      </div>
    </motion.article>
  );
}
